const META_FBC_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
export const META_FBC_MAX_AGE_MS = META_FBC_COOKIE_MAX_AGE_SECONDS * 1000;

const DEFAULT_FBC_SUBDOMAIN_INDEX = "1";
const MAX_FBC_CLOCK_SKEW_MS = 5 * 60 * 1000;
const PARAM_BUILDER_APPENDIX_RE = /^(?:[A-Za-z0-9]{2}|[A-Za-z0-9]{8})$/;

const getBrowserOrigin = () => {
  if (typeof window === "undefined" || !window.location?.origin) {
    return "https://ourlinkedlives.com";
  }
  return window.location.origin;
};

export const cleanFbclidValue = (value) => {
  const trimmed = String(value || "").trim();
  return trimmed || null;
};

function getSubdomainIndexFromHostname(hostname) {
  const cleanHostname = String(hostname || "").trim().toLowerCase();
  if (
    !cleanHostname ||
    cleanHostname === "localhost" ||
    /^\d{1,3}(?:\.\d{1,3}){3}$/.test(cleanHostname)
  ) {
    return DEFAULT_FBC_SUBDOMAIN_INDEX;
  }

  const labels = cleanHostname.split(".").filter(Boolean);
  if (labels.length < 2) return DEFAULT_FBC_SUBDOMAIN_INDEX;
  return String(labels.length - 1);
}

function getSubdomainIndexFromUrl(href) {
  if (!href) return DEFAULT_FBC_SUBDOMAIN_INDEX;

  try {
    const url = new URL(href, getBrowserOrigin());
    return getSubdomainIndexFromHostname(url.hostname);
  } catch (_error) {
    return DEFAULT_FBC_SUBDOMAIN_INDEX;
  }
}

export function extractFbclidFromUrl(href) {
  if (!href) return null;

  try {
    const url = new URL(href, getBrowserOrigin());
    return cleanFbclidValue(url.searchParams.get("fbclid"));
  } catch (_error) {
    return null;
  }
}

export function parseFbcValue(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return null;

  const parts = trimmed.split(".");
  if (parts.length < 4 || parts[0] !== "fb" || !/^\d+$/.test(parts[1])) {
    return null;
  }

  const creationTimeMs = Number(parts[2]);
  if (!Number.isFinite(creationTimeMs) || creationTimeMs < 1000000000000) {
    return null;
  }

  const hasParameterBuilderAppendix =
    parts.length >= 5 && PARAM_BUILDER_APPENDIX_RE.test(parts[parts.length - 1]);
  const clickIdParts = hasParameterBuilderAppendix
    ? parts.slice(3, -1)
    : parts.slice(3);
  const clickIdWithAppendix = parts.slice(3).join(".");
  const fbclid = cleanFbclidValue(clickIdParts.join("."));
  if (!fbclid) return null;

  return {
    value: trimmed,
    subdomainIndex: parts[1],
    creationTimeMs,
    fbclid,
    clickIdWithAppendix,
    appendix: hasParameterBuilderAppendix ? parts[parts.length - 1] : null,
  };
}

export function buildFbcValueFromFbclid(
  fbclid,
  nowMs = Date.now(),
  subdomainIndex = DEFAULT_FBC_SUBDOMAIN_INDEX,
) {
  const cleanFbclid = cleanFbclidValue(fbclid);
  if (!cleanFbclid) return null;

  const cleanSubdomainIndex = /^\d+$/.test(String(subdomainIndex || ""))
    ? String(subdomainIndex)
    : DEFAULT_FBC_SUBDOMAIN_INDEX;

  return `fb.${cleanSubdomainIndex}.${Math.floor(nowMs)}.${cleanFbclid}`;
}

function isFbcFresh(parsedFbc, nowMs) {
  if (!parsedFbc) return false;

  const ageMs = nowMs - parsedFbc.creationTimeMs;
  return ageMs >= -MAX_FBC_CLOCK_SKEW_MS && ageMs <= META_FBC_MAX_AGE_MS;
}

function fbcContainsClickId(parsedFbc, fbclid) {
  const cleanFbclid = cleanFbclidValue(fbclid);
  if (!parsedFbc || !cleanFbclid) return false;

  return (
    parsedFbc.fbclid === cleanFbclid ||
    parsedFbc.clickIdWithAppendix === cleanFbclid
  );
}

export function resolveFreshFbc({
  eventSourceUrl,
  currentFbc,
  storedFbclid,
  storedFbclidCapturedAtMs,
  nowMs = Date.now(),
} = {}) {
  const parsedCurrentFbc = parseFbcValue(currentFbc);
  const latestFbclid = extractFbclidFromUrl(eventSourceUrl);

  if (latestFbclid) {
    if (
      fbcContainsClickId(parsedCurrentFbc, latestFbclid) &&
      isFbcFresh(parsedCurrentFbc, nowMs)
    ) {
      return parsedCurrentFbc.value;
    }

    return buildFbcValueFromFbclid(
      latestFbclid,
      nowMs,
      parsedCurrentFbc?.subdomainIndex || getSubdomainIndexFromUrl(eventSourceUrl),
    );
  }

  const cleanStoredFbclid = cleanFbclidValue(storedFbclid);
  const storedCapturedAtMs = Number(storedFbclidCapturedAtMs);
  const storedFbclidAgeMs = nowMs - storedCapturedAtMs;
  const hasFreshStoredFbclid =
    cleanStoredFbclid &&
    Number.isFinite(storedCapturedAtMs) &&
    storedCapturedAtMs >= 1000000000000 &&
    storedFbclidAgeMs >= -MAX_FBC_CLOCK_SKEW_MS &&
    storedFbclidAgeMs <= META_FBC_MAX_AGE_MS;

  if (
    hasFreshStoredFbclid &&
    fbcContainsClickId(parsedCurrentFbc, cleanStoredFbclid) &&
    isFbcFresh(parsedCurrentFbc, nowMs)
  ) {
    return parsedCurrentFbc.value;
  }

  if (
    hasFreshStoredFbclid &&
    (!isFbcFresh(parsedCurrentFbc, nowMs) ||
      storedCapturedAtMs >= parsedCurrentFbc.creationTimeMs)
  ) {
    return buildFbcValueFromFbclid(
      cleanStoredFbclid,
      storedCapturedAtMs,
      parsedCurrentFbc?.subdomainIndex || getSubdomainIndexFromUrl(eventSourceUrl),
    );
  }

  if (!parsedCurrentFbc) return null;

  if (!isFbcFresh(parsedCurrentFbc, nowMs)) {
    return null;
  }

  return parsedCurrentFbc.value;
}

export function writeFbcCookie(fbc) {
  if (typeof document === "undefined" || !fbc) return;

  const secure =
    typeof window !== "undefined" && window.location?.protocol === "https:"
      ? "; Secure"
      : "";

  document.cookie = `_fbc=${encodeURIComponent(
    fbc,
  )}; path=/; max-age=${META_FBC_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}
